import { useState, useEffect, useRef } from "react";
import { authenticatedFetch } from "./api";
import SearchableSelect from "./SearchableSelect";
import "./ShopifySkuMatch.css";

function ShopifySkuMatch() {
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState("");
  const [file, setFile] = useState(null);

  // Sync Metadata & History
  const [syncMeta, setSyncMeta] = useState(null);
  const [history, setHistory] = useState([]);
  const [historySummary, setHistorySummary] = useState({ total_runs: 0, completed: 0, partial: 0, failed: 0 });

  // Active Job State
  const [jobId, setJobId] = useState(null);
  const [jobType, setJobType] = useState(null); // 'sync' | 'compare'
  const [percent, setPercent] = useState(0);
  const [message, setMessage] = useState("");
  const [jobStatus, setJobStatus] = useState("idle"); // idle | processing | done | error

  // Delete Confirmation Modal State
  const [deleteModalJob, setDeleteModalJob] = useState(null); // { id, vendor }

  // View Output Sheet Modal State
  const [viewModalData, setViewModalData] = useState(null);
  const [viewModalLoading, setViewModalLoading] = useState(false);
  const [viewSheet, setViewSheet] = useState("missing"); // 'missing' | 'styles'
  const [viewSearchTerm, setViewSearchTerm] = useState("");
  const [viewPage, setViewPage] = useState(1);
  const VIEW_PAGE_SIZE = 25;

  const pollRef = useRef(null);

  // Fast Refresh of History & Sync Status (without re-fetching vendor dropdown list)
  const refreshHistoryAndStatus = async () => {
    try {
      const [sRes, hRes] = await Promise.all([
        authenticatedFetch("/sku-match/sync-status"),
        authenticatedFetch("/sku-match/history")
      ]);

      if (sRes.ok) {
        const sData = await sRes.json();
        setSyncMeta(sData.sync);
      }

      if (hRes.ok) {
        const hData = await hRes.json();
        setHistory(hData.history || []);
        setHistorySummary(hData.summary || { total_runs: 0, completed: 0, partial: 0, failed: 0 });
      }
    } catch (err) {
      console.error("Error refreshing status:", err);
    }
  };

  // Load Vendors, Sync Status, and Comparison History concurrently on initial page load
  const loadConsoleData = async () => {
    try {
      const [vRes] = await Promise.all([
        authenticatedFetch("/vendors")
      ]);

      if (vRes.ok) {
        const vData = await vRes.json();
        setVendors(Array.from(new Set(vData.vendors || [])));
      }

      await refreshHistoryAndStatus();
    } catch (err) {
      console.error("Error loading console data:", err);
    }
  };

  useEffect(() => {
    loadConsoleData();
  }, []);

  // Poll progress for active job
  useEffect(() => {
    if (!jobId) return;

    pollRef.current = setInterval(async () => {
      try {
        const res = await authenticatedFetch(`/progress/${jobId}`);
        if (!res.ok) return;

        const data = await res.json();
        setPercent(data.percent || 0);
        setMessage(data.message || "");

        if (data.status === "done" || data.status === "error") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          // Fast refresh history and status FIRST before ending processing state
          await refreshHistoryAndStatus();
          setJobStatus(data.status);
        } else {
          setJobStatus(data.status || "processing");
        }
      } catch (err) {
        console.error("Polling error:", err);
      }
    }, 1000);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [jobId]);

  // Handle Catalog Refresh Trigger
  const handleCatalogSync = async () => {
    if (jobStatus === "processing") return;
    if (!window.confirm("Start refreshing full Shopify catalog from Shopify GraphQL API?")) return;

    try {
      setJobType("sync");
      setJobStatus("processing");
      setPercent(2);
      setMessage("Connecting to Shopify...");

      const res = await authenticatedFetch("/sku-match/sync", { method: "POST" });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.detail || "Failed to start catalog sync.");
      }

      const data = await res.json();
      setJobId(data.job_id);
    } catch (err) {
      alert(`Error starting catalog sync: ${err.message}`);
      setJobStatus("error");
      setMessage(err.message);
    }
  };

  // Handle SKU Comparison Trigger
  const handleCompare = async () => {
    if (!selectedVendor || !file || jobStatus === "processing") return;

    try {
      setJobType("compare");
      setJobStatus("processing");
      setPercent(5);
      setMessage(`Uploading file for ${selectedVendor}...`);

      const formData = new FormData();
      formData.append("vendor_name", selectedVendor);
      formData.append("file", file);

      const res = await authenticatedFetch("/sku-match/compare", {
        method: "POST",
        body: formData
      });

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.detail || "Failed to start SKU comparison.");
      }

      const data = await res.json();
      setJobId(data.job_id);
    } catch (err) {
      alert(`Error starting comparison: ${err.message}`);
      setJobStatus("error");
      setMessage(err.message);
    }
  };

  // Handle Excel Download for specific job_id
  const handleDownload = async (targetJobId, filename) => {
    try {
      const res = await authenticatedFetch(`/sku-match/download/${targetJobId}`);
      if (!res.ok) throw new Error("Report unavailable.");

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "missing_skus.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(`Download Error: ${err.message}`);
    }
  };

  // Handle Deleting History Record from Database via Modal Confirmation
  const confirmDeleteHistory = async () => {
    if (!deleteModalJob) return;

    try {
      const res = await authenticatedFetch(`/sku-match/history/${deleteModalJob.id}`, {
        method: "DELETE"
      });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.detail || "Failed to delete record.");
      }

      setDeleteModalJob(null);
      await loadConsoleData();
    } catch (err) {
      alert(`Delete Error: ${err.message}`);
    }
  };

  // Handle Fetching Output Sheet Data for Preview Modal
  const handleViewHistory = async (targetJobId, sheet = "missing") => {
    setViewModalLoading(true);
    setViewSheet(sheet);
    setViewSearchTerm("");
    setViewPage(1);
    try {
      const res = await authenticatedFetch(`/sku-match/view/${targetJobId}?sheet=${sheet}`);
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.detail || "Failed to load report preview.");
      }

      const data = await res.json();
      setViewModalData(data);
    } catch (err) {
      alert(`View Error: ${err.message}`);
    } finally {
      setViewModalLoading(false);
    }
  };

  // Format sync date
  const formatSyncTime = () => {
    if (!syncMeta || !syncMeta.last_sync) return null;
    const d = new Date(syncMeta.last_sync);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Format history date (e.g. Jul 30, 10:42 AM)
  const formatHistoryDate = (isoStr) => {
    if (!isoStr) return "—";
    const d = new Date(isoStr);
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ", " + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="console-layout">
      {/* ─── Header Bar (44-48px, Light Theme, Single Line) ────────────────────────────────────────── */}
      <header className="console-header">
        <div className="console-breadcrumb">
          <span className="brand-icon">⚡</span>
          <span>SKU Match</span>
          <span>&gt;</span>
          <span className="active-crumb">Operations</span>
        </div>

        <div className="console-catalog-status">
          <span className="catalog-title-group">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <path d="M16 10a4 4 0 0 1-8 0"></path>
            </svg>
            Shopify Catalog
          </span>

          <span className="bullet-sep">•</span>

          <div className={`status-indicator ${jobStatus === "processing" && jobType === "sync" ? "syncing" : "healthy"}`}>
            <span className="dot-pulse" />
            <span>{jobStatus === "processing" && jobType === "sync" ? "Syncing..." : "Healthy"}</span>
          </div>

          <span className="bullet-sep">•</span>

          <span className="catalog-metric">
            {(syncMeta?.variants_count || 0).toLocaleString()} variants
          </span>

          <span className="bullet-sep">•</span>

          <span className="catalog-metric">
            {syncMeta?.last_sync ? `synced ${formatSyncTime()}` : "Not synced yet"}
          </span>

          <span className="bullet-sep">•</span>

          <button
            className="refresh-icon-btn"
            onClick={handleCatalogSync}
            disabled={jobStatus === "processing"}
            title="Refresh Shopify Catalog"
          >
            <span>🔄</span>
            <span>Refresh</span>
          </button>

          <span className="bullet-sep">•</span>

          <span className="store-badge">
            <span className="store-dot" />
            <span>Store TDO</span>
          </span>
        </div>
      </header>

      {/* ─── Split Body Layout ─────────────────────────────────────────────── */}
      <div className="console-body">
        {/* Left Operations Panel (240px) */}
        <aside className="left-ops-panel">
          <div className="ops-section-title">COMPARE INVENTORY</div>

          {/* Vendor Dropdown */}
          <div className="ops-field-group">
            <label>VENDOR</label>
            <SearchableSelect
              options={vendors}
              value={selectedVendor}
              onChange={(val) => setSelectedVendor(val)}
              placeholder="Select vendor..."
              disabled={jobStatus === "processing"}
            />
          </div>

          {/* Upload Area */}
          <div className="ops-field-group">
            <label>INVENTORY FILE (.XLSX)</label>
            <div className="ops-file-drop">
              <input
                type="file"
                accept=".xlsx, .xls"
                onChange={(e) => setFile(e.target.files[0])}
                disabled={jobStatus === "processing"}
              />
              <div className="drop-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
              </div>
              <div className="drop-text">
                {file ? "Change File" : <span>Drop file here <br />or <span className="browse-link">browse</span></span>}
              </div>
              {file && (
                <div className="file-name-tag">
                  {file.name}
                </div>
              )}
            </div>
          </div>

          {/* Compare SKUs Button - Directly Below Upload Box */}
          <button
            className="compare-btn"
            onClick={handleCompare}
            disabled={jobStatus === "processing" || !selectedVendor || !file}
          >
            <span>✨</span>
            <span>
              {jobStatus === "processing" && jobType === "compare"
                ? `Comparing... (${percent}%)`
                : "Compare & Verify SKUs"}
            </span>
          </button>
          <div className="button-subtext">Select vendor + upload file</div>

          {/* Active Job Progress Display */}
          {jobStatus === "processing" && (
            <div className="panel-progress" style={{ marginTop: "8px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.75rem", color: "#2563eb", marginBottom: "4px", fontWeight: 600 }}>
                <span>{message}</span>
                <span>{percent}%</span>
              </div>
              <div style={{ height: "5px", background: "#e2e8f0", borderRadius: "4px", overflow: "hidden" }}>
                <div style={{ width: `${percent}%`, height: "100%", background: "#2563eb", borderRadius: "4px", transition: "width 0.3s" }} />
              </div>
            </div>
          )}
        </aside>

        {/* Right History / Results Panel */}
        <main className="right-ops-panel">
          {history.length === 0 && jobStatus !== "processing" ? (
            <div className="empty-history-state">
              <div className="empty-icon">📊</div>
              <div className="empty-title">No comparison history yet.</div>
              <p style={{ fontSize: "0.8rem", color: "#94a3b8", marginBottom: "1rem" }}>
                Select a vendor and upload an Excel sheet to run your first SKU comparison.
              </p>
            </div>
          ) : (
            <div className="table-container">
              <table className="history-table">
                <thead>
                  <tr>
                    <th>STATUS</th>
                    <th>VENDOR</th>
                    <th>DATE</th>
                    <th>UPLOADED</th>
                    <th>FOUND</th>
                    <th>MISSING</th>
                    <th>STYLE ISSUES</th>
                    <th>DURATION</th>
                    <th style={{ textAlign: "center" }}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {/* If active job is processing, render active row first */}
                  {jobStatus === "processing" && jobType === "compare" && (
                    <tr className="history-row" style={{ background: "#eff6ff" }}>
                      <td>
                        <span className="badge badge-running">
                          <span className="spinner-inline" /> Syncing...
                        </span>
                      </td>
                      <td style={{ fontWeight: 600, color: "#0f172a" }}>{selectedVendor}</td>
                      <td>Just now</td>
                      <td>—</td>
                      <td>—</td>
                      <td>—</td>
                      <td>—</td>
                      <td>{percent}%</td>
                      <td style={{ textAlign: "center" }}>
                        <span className="running-job-pill">⏳ Processing ({percent}%)</span>
                      </td>
                    </tr>
                  )}

                  {history.map((row) => {
                    const isCompleted = row.status === "completed";
                    const isPartial = isCompleted && row.missing_count > 0;
                    const isClean = isCompleted && row.missing_count === 0;
                    const isFailed = row.status === "failed" || row.status === "error";

                    return (
                      <tr key={row.job_id} className="history-row">
                        <td>
                          {isClean && (
                            <span className="badge badge-completed">
                              <span className="badge-dot" /> Completed
                            </span>
                          )}
                          {isPartial && (
                            <span className="badge badge-partial">
                              <span className="badge-dot" /> Partial
                            </span>
                          )}
                          {isFailed && (
                            <span className="badge badge-failed">
                              <span className="badge-dot" /> Failed
                            </span>
                          )}
                        </td>
                        <td style={{ fontWeight: 600, color: "#0f172a" }}>{row.vendor_name}</td>
                        <td>{formatHistoryDate(row.completed_at)}</td>
                        <td>{(row.uploaded_count || 0).toLocaleString()}</td>
                        <td>{(row.found_count || 0).toLocaleString()}</td>
                        <td>
                          {row.missing_count > 0 ? (
                            <span className="missing-count-high">{row.missing_count.toLocaleString()}</span>
                          ) : (
                            <span className="missing-count-zero">—</span>
                          )}
                        </td>
                        <td>
                          {row.styles_missing_count > 0 ? (
                            <span className="missing-count-high">{row.styles_missing_count.toLocaleString()}</span>
                          ) : (
                            <span className="missing-count-zero">—</span>
                          )}
                        </td>
                        <td>{row.duration_seconds ? `${row.duration_seconds}s` : "—"}</td>
                        <td style={{ textAlign: "center" }}>
                          <div className="action-buttons-group">
                            {/* View Output Button */}
                            <button
                              className="tbl-action-btn view-btn"
                              onClick={() => handleViewHistory(row.job_id)}
                              title="View output sheet data"
                            >
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                <circle cx="12" cy="12" r="3"></circle>
                              </svg>
                              <span>View</span>
                            </button>

                            {/* Download Button */}
                            <button
                              className="tbl-action-btn download-btn"
                              disabled={!row.download_available}
                              onClick={() => handleDownload(row.job_id, row.download_filename)}
                              title={row.download_available ? "Download Report" : "Report unavailable"}
                            >
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                <polyline points="7 10 12 15 17 10"/>
                                <line x1="12" y1="15" x2="12" y2="3"/>
                              </svg>
                              <span>Download</span>
                            </button>

                            {/* Delete Button */}
                            <button
                              className="tbl-action-btn delete-btn"
                              onClick={() => setDeleteModalJob({ id: row.job_id, vendor: row.vendor_name })}
                              title="Delete comparison record"
                            >
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6"></polyline>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Footer Bar */}
          <footer className="console-footer">
            <div className="footer-left">
              <span>{historySummary.total_runs || history.length} runs</span>
              <span>•</span>
              <span>last 30 days</span>
            </div>

            <div className="footer-right">
              <span>{historySummary.completed || 0} completed</span>
              <span>•</span>
              <span>{historySummary.failed || 0} failed</span>
              <span>•</span>
              <span>{historySummary.partial || 0} partial</span>
            </div>
          </footer>
        </main>
      </div>

      {/* Delete Confirmation Modal Overlay */}
      {deleteModalJob && (
        <div className="modal-overlay" onClick={() => setDeleteModalJob(null)}>
          <div className="confirm-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-icon-badge">🗑️</div>
            <h3 className="confirm-modal-title">Delete Comparison Record</h3>
            <p className="confirm-modal-text">
              Are you sure you want to delete comparison record for <strong>{deleteModalJob.vendor}</strong>?
            </p>
            <div className="confirm-modal-actions">
              <button
                className="confirm-cancel-btn"
                onClick={() => setDeleteModalJob(null)}
              >
                Cancel
              </button>
              <button
                className="confirm-delete-btn"
                onClick={confirmDeleteHistory}
              >
                Delete Record
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Output Sheet Modal Overlay */}
      {viewModalData && (
        <div className="modal-overlay" onClick={() => setViewModalData(null)}>
          <div className="view-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="view-modal-header">
              <div className="header-title-area">
                <span className="view-modal-icon">📄</span>
                <div>
                  <h3 className="view-modal-title">
                    {viewModalData.vendor_name} — Comparison Output Report
                  </h3>
                  <div className="view-modal-subtitle">
                    Job ID: <span className="mono-id">{viewModalData.job_id}</span>
                  </div>
                </div>
              </div>

              <div className="header-actions">
                {viewModalData.download_available && (
                  <button
                    className="modal-download-primary-btn"
                    onClick={() => handleDownload(viewModalData.job_id, `${viewModalData.vendor_name}_missing_skus.xlsx`)}
                  >
                    <span>📥</span>
                    <span>Download Report</span>
                  </button>
                )}
                <button
                  className="close-modal-x-btn"
                  onClick={() => setViewModalData(null)}
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Metrics Summary Strip */}
            <div className="view-metrics-strip">
              <div className="metric-pill">
                <span className="pill-label">Uploaded</span>
                <span className="pill-val">{(viewModalData.uploaded_count || 0).toLocaleString()}</span>
              </div>
              <div className="metric-pill">
                <span className="pill-label">Found</span>
                <span className="pill-val">{(viewModalData.found_count || 0).toLocaleString()}</span>
              </div>
              <div className="metric-pill highlight-missing">
                <span className="pill-label">Missing SKUs</span>
                <span className="pill-val">{(viewModalData.missing_count || 0).toLocaleString()}</span>
              </div>
              <div className={`metric-pill ${viewModalData.styles_missing_count > 0 ? "highlight-missing" : ""}`}>
                <span className="pill-label">Missing Styles</span>
                <span className="pill-val">{(viewModalData.styles_missing_count || 0).toLocaleString()}</span>
              </div>
            </div>

            {/* Sheet Tabs */}
            <div className="view-sheet-tabs">
              <button
                className={`view-sheet-tab ${viewSheet === "missing" ? "active" : ""}`}
                onClick={() => handleViewHistory(viewModalData.job_id, "missing")}
              >
                Missing SKUs ({(viewModalData.missing_count || 0).toLocaleString()})
              </button>
              <button
                className={`view-sheet-tab ${viewSheet === "styles" ? "active" : ""}`}
                onClick={() => handleViewHistory(viewModalData.job_id, "styles")}
              >
                Missing Styles ({(viewModalData.styles_missing_count || 0).toLocaleString()})
              </button>
            </div>

            {/* Search Filter & Info Banner */}
            <div className="view-search-bar">
              <span className="search-icon">🔍</span>
              <input
                type="text"
                className="view-search-input"
                placeholder={viewSheet === "styles" ? "Filter rows by SKU, style, or status..." : "Filter output sheet rows by SKU or status..."}
                value={viewSearchTerm}
                onChange={(e) => {
                  setViewSearchTerm(e.target.value);
                  setViewPage(1);
                }}
              />
              {viewSearchTerm && (
                <button
                  className="clear-view-search"
                  onClick={() => {
                    setViewSearchTerm("");
                    setViewPage(1);
                  }}
                >
                  ✕
                </button>
              )}
            </div>

            {/* Info Banner for Large Datasets */}
            {viewModalData.total_preview_rows > 200 && (
              <div className="view-preview-banner">
                <span>ℹ️</span>
                <span>
                  Showing fast preview of top <strong>200</strong> {viewSheet === "styles" ? "style issues" : "missing SKUs"} out of <strong>{viewModalData.total_preview_rows.toLocaleString()}</strong> total. Click <strong>Download Report</strong> above to export all rows to Excel.
                </span>
              </div>
            )}

            {/* Output Sheet Table */}
            <div className="view-table-wrapper">
              {viewModalData.rows.length === 0 ? (
                <div className="view-empty-state">
                  <span className="empty-emoji">🎉</span>
                  {viewSheet === "styles" ? (
                    <>
                      <h4>All Styles & Variants Complete!</h4>
                      <p>No missing SKUs were flagged for missing styles or variants during this comparison run.</p>
                    </>
                  ) : (
                    <>
                      <h4>All SKUs Found & Verified!</h4>
                      <p>No missing SKUs were identified during this comparison run.</p>
                    </>
                  )}
                </div>
              ) : (() => {
                const filteredRows = viewModalData.rows.filter((r) =>
                  Object.values(r).some((val) =>
                    String(val).toLowerCase().includes(viewSearchTerm.toLowerCase())
                  )
                );

                const totalPages = Math.ceil(filteredRows.length / VIEW_PAGE_SIZE) || 1;
                const currentPage = Math.min(viewPage, totalPages);
                const startIndex = (currentPage - 1) * VIEW_PAGE_SIZE;
                const pageRows = filteredRows.slice(startIndex, startIndex + VIEW_PAGE_SIZE);

                return (
                  <>
                    <table className="view-data-table">
                      <thead>
                        <tr>
                          {Object.keys(viewModalData.rows[0]).map((colKey) => (
                            <th key={colKey}>{colKey.toUpperCase()}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {pageRows.map((r, rowIdx) => (
                          <tr key={rowIdx}>
                            {Object.entries(r).map(([colKey, val], colIdx) => (
                              <td key={colIdx}>
                                {colKey.toLowerCase() === "status" ? (
                                  <span className={`badge ${["missing", "not found"].includes(String(val).toLowerCase()) ? "badge-failed" : "badge-completed"}`}>
                                    {val}
                                  </span>
                                ) : (
                                  String(val) || "—"
                                )}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Pagination Footer */}
                    {filteredRows.length > VIEW_PAGE_SIZE && (
                      <div className="view-pagination-footer">
                        <button
                          className="page-nav-btn"
                          disabled={currentPage === 1}
                          onClick={() => setViewPage((p) => Math.max(1, p - 1))}
                        >
                          ‹ Previous
                        </button>
                        <span className="page-info-text">
                          Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong> ({filteredRows.length} rows)
                        </span>
                        <button
                          className="page-nav-btn"
                          disabled={currentPage === totalPages}
                          onClick={() => setViewPage((p) => Math.min(totalPages, p + 1))}
                        >
                          Next ›
                        </button>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ShopifySkuMatch;
